import { all, call, put, select, takeEvery } from "redux-saga/effects";
import { SagaIterator } from "redux-saga";
import * as approvalActions from "./approvalsActions";
import { ApprovalI,
  CreateDedicatedOfferI,
  CreateSingleOfferI,
  DeleteApprovalQueueI,
  UpdateOfferQueueI,
  UpdateStatusQueueI,
  approvalsTypes } from "./approvalsTypes";
import ApprovalsService from "./service";
import { notify } from "../notification/notificationActions";
import { approvalHistory, approvalQueue } from "../realTimeData/selector";
import { resetStack } from "../realTimeData/actions";
import { approvalsFiltersSelector, approvalsHistoryFiltersSelector } from "./approvalsSelector";
import { isEqual } from "lodash";
import { initialValues } from "../../config/validations/approvals";
import { parseFilterPayload } from "./helpers";

export function* getApprovalsQueue({ payload }: any): SagaIterator {
  try {
    const { data } = yield call<typeof ApprovalsService.getApprovalsQueue>(ApprovalsService.getApprovalsQueue, payload);

    yield put(approvalActions.getApprovalsQueueSuccess(data));
  } catch (error: any) {
    yield put(approvalActions.getApprovalsQueueFailure(error?.message));
    yield put(notify({ message: error?.message, type: "error" }));
  }
}

export function* onGetApprovalsQueue() {
  yield takeEvery(approvalsTypes.GET_APPROVALS_QUEUE, getApprovalsQueue);
}

export function* createDedicatedOffer({ payload, cbSuccess, cbFailure }: CreateDedicatedOfferI): SagaIterator {
  try {
    const { data } = yield call<typeof ApprovalsService.createDedicatedOffer>(
      ApprovalsService.createDedicatedOffer,
      payload,
    );

    yield put(approvalActions.createDedicatedOfferSuccess(data));
    yield call(cbSuccess);
  } catch (error: any) {
    yield put(approvalActions.createDedicatedOfferFailure(error?.message));
    yield put(notify({ message: error?.message, type: "error" }));
    yield call(cbFailure, error?.mesasge);
  }
}

export function* onCreateDedicatedOffer() {
  yield takeEvery(approvalsTypes.CREATE_DEDICATED_OFFER, createDedicatedOffer);
}

export function* createSingleOffer({ payload, cbSuccess, cbFailure }: CreateSingleOfferI): SagaIterator {
  try {
    const { data } = yield call<typeof ApprovalsService.createSingleOffer>(ApprovalsService.createSingleOffer, payload);

    yield put(approvalActions.createSingleOfferSuccess(data));
    yield call(cbSuccess);
  } catch (error: any) {
    yield put(approvalActions.createSingleOfferFailure(error?.message));
    yield call(cbFailure, error?.mesasge);
  }
}

export function* onCreateSingleOffer() {
  yield takeEvery(approvalsTypes.CREATE_SINGLE_OFFER, createSingleOffer);
}

export function* updateOfferQueue({ payload, cbSuccess, cbFailure }: UpdateOfferQueueI): SagaIterator {
  try {
    const { data } = yield call<typeof ApprovalsService.updateOfferQueue>(ApprovalsService.updateOfferQueue, payload);

    yield put(approvalActions.updateOfferQueueSuccess(data));
    yield call(cbSuccess);
  } catch (error: any) {
    yield put(approvalActions.updateOfferQueueFailure(error?.message));
    yield call(cbFailure, error?.message);
  }
}

export function* onUpdateOfferQueue() {
  yield takeEvery(approvalsTypes.UPDATE_OFFER_QUEUE, updateOfferQueue);
}

export function* deleteApprovalQueue({
  payload,
  successCallback,
  failureCallback,
}: DeleteApprovalQueueI): SagaIterator {
  try {
    const { data } = yield call<typeof ApprovalsService.deleteApprovalQueue>(
      ApprovalsService.deleteApprovalQueue,
      payload,
    );

    yield put(approvalActions.deleteApprovalQueueSuccess(data));
    yield call(successCallback);
  } catch (error: any) {
    yield put(approvalActions.deleteApprovalQueueFailure(error?.message));
    yield call(failureCallback, error?.message);
  }
}

export function* onDeleteApprovalQueue() {
  yield takeEvery(approvalsTypes.DELETE_APPROVAL_QUEUE, deleteApprovalQueue);
}

export function* updateStatusQueue({ payload, successCallback, failureCallback }: UpdateStatusQueueI): SagaIterator {
  try {
    const { approval_id } = payload;
    const { data } = yield call<typeof ApprovalsService.updateStatusQueue>(
      ApprovalsService.updateStatusQueue,
      payload,
      approval_id,
    );

    yield put(approvalActions.updateStatusQueueSuccess(data));
    yield call(successCallback);
  } catch (error: any) {
    yield put(approvalActions.updateStatusQueueFailure(error?.message));
    yield call(failureCallback, error?.message);
  }
}

export function* onUpdateStatusQueue() {
  yield takeEvery(approvalsTypes.UPDATE_STATUS_QUEUE, updateStatusQueue);
}

export function* getApprovalsHistory({ payload }: any): SagaIterator {
  try {
    const { data } = yield call<typeof ApprovalsService.getApprovalsHistory>(
      ApprovalsService.getApprovalsHistory,
      payload,
    );

    yield put(approvalActions.getApprovalsHistorySuccess(data));
  } catch (error: any) {
    yield put(approvalActions.getApprovalsHistoryFailure(error?.message));
    yield put(notify({ message: error?.message, type: "error" }));
  }
}

export function* onGetApprovalsHistory() {
  yield takeEvery(approvalsTypes.GET_APPROVALS_HISTORY, getApprovalsHistory);
}

export function* showLastData({ payload }: ReturnType<typeof approvalActions.showLastData>): SagaIterator {
  try {
    const approvalsFilters = yield select(
      payload.section === "queue" ? approvalsFiltersSelector : approvalsHistoryFiltersSelector,
    );
    let approvalsFiltered: Array<ApprovalI> = yield select(payload.section === "queue" ? approvalQueue : approvalHistory);

    if (!isEqual({ ...approvalsFilters, page: undefined }, { ...initialValues, page: undefined })) {
      const { data } = yield call<typeof ApprovalsService.getApprovalsQueue>(ApprovalsService.getApprovalsQueue, {
        ...parseFilterPayload(approvalsFilters),
        approval_id: approvalsFiltered.map((approval) => approval.approval_id),
      });

      approvalsFiltered = data || [];
    }

    const approvals = approvalsFiltered.map((approval: any) => ({ ...approval, isLast: true }));
    const showMessage = approvalsFiltered.length > approvals.length;

    yield put(approvalActions.showLastDataSuccess({ approvals, showMessage, section: payload.section }));
    yield put(resetStack(payload.section));
    yield put(notify({ message: `Approval ${payload.section} was update`, type: "info" }));
  } catch (error: any) {
    yield put(approvalActions.showLastDataFailure(error.message));
    yield put(notify({ message: error?.message, type: "error" }));
  }
}

export function* onGetLastApprovalsPusher() {
  yield takeEvery(approvalsTypes.SHOW_LAST_DATA, showLastData);
}

export default function* approvalsSaga() {
  yield all([
    call(onGetApprovalsQueue),
    call(onCreateSingleOffer),
    call(onCreateDedicatedOffer),
    call(onDeleteApprovalQueue),
    call(onUpdateStatusQueue),
    call(onUpdateOfferQueue),
    call(onGetApprovalsHistory),
    call(onGetLastApprovalsPusher),
  ]);
}
